import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Vehicle card ordering + drag-to-reorder (RON-5907 change 4).
//
// The Profile vehicle tab renders two different kinds of card from two different
// state slices: saved vehicles (`vehicles`, keyed on the numeric API `Id`) and
// unsaved add-vehicle drafts (`newVehicles`, keyed on a client-side `uid`).
// The two arrays are deliberately NOT merged — `updateVehicles()` maps over
// `vehicles` and would try to PUT rows that do not exist server-side yet.
//
// Instead this hook keeps a single array of order keys ("v-59353" / "d-3") and
// drag only ever reorders that array. Every existing handler keeps working:
// saved handlers still key off `vehicle.Id`, draft handlers still receive their
// real index inside `newVehicles`, which reordering never changes.
//
// Dragging is implemented with pointer events rather than native HTML5
// drag-and-drop: the design calls for the real card to lift out of the list and
// float above its neighbours, and the native drag image is a browser-generated
// bitmap that is rendered ghosted, has outer shadows clipped off it, and cannot
// overlap the list. Pointer events move the actual element, so the lifted look
// is ordinary CSS.
const SAVED_PREFIX = "v";
const DRAFT_PREFIX = "d";

export const savedCardKey = (vehicle) => `${SAVED_PREFIX}-${vehicle?.Id}`;
export const draftCardKey = (draft) => `${DRAFT_PREFIX}-${draft?.uid}`;

const normalizeRegistration = (value) => String(value ?? "").trim().toLowerCase();

const sameOrder = (a, b) => a.length === b.length && a.every((key, index) => key === b[index]);

// A drag can start anywhere on the card except on a control the user still needs
// the mouse for: the registration input (caret placement / text selection), the
// size and fuel dropdowns, and the remove link. The drag handle is always a
// valid start point even though it is a button.
const NON_DRAGGABLE_SELECTOR =
  'input, textarea, select, button, a, [contenteditable="true"], .dropdown-menu';

const canStartDragFrom = (target) => {
  if (!target || typeof target.closest !== "function") return true;
  if (target.closest("[data-vehicle-drag-handle]")) return true;
  return !target.closest(NON_DRAGGABLE_SELECTOR);
};

// Drag tuning.
const DRAG_START_THRESHOLD = 4; // px of movement before a press becomes a drag
const AUTO_SCROLL_EDGE = 56; // px from the scroll container's edge
const AUTO_SCROLL_STEP = 14; // px per frame
const CARD_GAP = 16; // .vehicle-card margin-bottom
const LIFT_SCALE = 1.02;
const SLIDE_TRANSITION = "transform 180ms ease";
const SCROLL_CONTAINER_SELECTOR = ".height-wrapper";

const raf = (callback) =>
  typeof requestAnimationFrame === "function" ? requestAnimationFrame(callback) : null;

const cancelRaf = (id) => {
  if (id != null && typeof cancelAnimationFrame === "function") cancelAnimationFrame(id);
};

// Where the dragged card lands: the number of other cards whose centre sits
// above the dragged card's current centre. That count is the insertion index in
// the array with the dragged card removed, which is its final index.
export const resolveDropIndex = ({ centers = [], fromIndex, deltaY = 0 }) => {
  if (fromIndex == null || fromIndex < 0 || fromIndex >= centers.length) return fromIndex;

  const draggedCenter = centers[fromIndex] + deltaY;
  let dropIndex = 0;
  centers.forEach((center, index) => {
    if (index !== fromIndex && center < draggedCenter) dropIndex += 1;
  });
  return dropIndex;
};

export default function useVehicleCardOrder({ vehicles, newVehicles, showDrafts }) {
  const [orderKeys, setOrderKeys] = useState([]);
  // The card currently being dragged — drives the lifted styling.
  const [dragKey, setDragKey] = useState(null);
  const [orderChanged, setOrderChanged] = useState(false);

  const orderKeysRef = useRef([]);
  // key -> card DOM node, so a drag can measure and transform without re-rendering.
  const nodesRef = useRef(new Map());
  const sessionRef = useRef(null);
  // Set right after a successful save so the next sync adopts the server order
  // verbatim instead of preserving the local one (a saved draft has to move out
  // of the draft slot and into its persisted position).
  const adoptServerOrderRef = useRef(false);

  orderKeysRef.current = orderKeys;

  // Keep the order in sync with both source arrays without losing the user's
  // manual order: keep known keys where they are, append what is new, drop what
  // is gone. Returns the previous array untouched when nothing moved so an
  // unrelated field edit does not trigger a re-render of the whole list.
  useEffect(() => {
    const liveKeys = [
      ...(vehicles || []).map(savedCardKey),
      ...(showDrafts ? (newVehicles || []).map(draftCardKey) : []),
    ];

    setOrderKeys((previousKeys) => {
      if (adoptServerOrderRef.current) {
        adoptServerOrderRef.current = false;
        return sameOrder(liveKeys, previousKeys) ? previousKeys : liveKeys;
      }

      const liveSet = new Set(liveKeys);
      const kept = previousKeys.filter((key) => liveSet.has(key));
      const keptSet = new Set(kept);
      const appended = liveKeys.filter((key) => !keptSet.has(key));
      const nextKeys = [...kept, ...appended];

      return sameOrder(nextKeys, previousKeys) ? previousKeys : nextKeys;
    });
  }, [vehicles, newVehicles, showDrafts]);

  // Ordered render list: one entry per visible card, carrying the data the JSX
  // needs. `draftIndex` is the real index inside `newVehicles`, so the existing
  // draft handlers (validation status, size popup, fuel dropdown) stay valid.
  const cards = useMemo(() => {
    const savedById = new Map((vehicles || []).map((vehicle) => [savedCardKey(vehicle), vehicle]));
    const draftsByKey = new Map(
      (newVehicles || []).map((draft, draftIndex) => [draftCardKey(draft), { draft, draftIndex }])
    );

    return orderKeys
      .map((key) => {
        const vehicle = savedById.get(key);
        if (vehicle) {
          return { key, kind: "saved", vehicle };
        }
        const draftEntry = showDrafts ? draftsByKey.get(key) : undefined;
        if (draftEntry) {
          return { key, kind: "draft", draft: draftEntry.draft, draftIndex: draftEntry.draftIndex };
        }
        return null;
      })
      .filter(Boolean);
  }, [orderKeys, vehicles, newVehicles, showDrafts]);

  const moveCardToIndex = useCallback((key, dropIndex) => {
    setOrderKeys((previousKeys) => {
      const fromIndex = previousKeys.indexOf(key);
      if (fromIndex === -1 || fromIndex === dropIndex) return previousKeys;

      const nextKeys = [...previousKeys];
      nextKeys.splice(fromIndex, 1);
      nextKeys.splice(dropIndex, 0, key);
      return nextKeys;
    });
    setOrderChanged(true);
  }, []);

  const moveCardBy = useCallback((key, offset) => {
    const currentKeys = orderKeysRef.current;
    const fromIndex = currentKeys.indexOf(key);
    const dropIndex = fromIndex + offset;
    if (fromIndex === -1 || dropIndex < 0 || dropIndex >= currentKeys.length) return false;

    moveCardToIndex(key, dropIndex);
    return true;
  }, [moveCardToIndex]);

  // --- drag session -------------------------------------------------------

  const scrollOffset = useCallback((session) => {
    if (!session.scrollEl) return 0;
    return session.scrollEl.scrollTop - session.startScrollTop;
  }, []);

  const deltaFor = useCallback(
    (session) => session.clientY - session.startY + scrollOffset(session),
    [scrollOffset]
  );

  // Moves the dragged card under the pointer and slides the cards it would
  // displace out of its way. Done by mutating style directly: a drag must not
  // re-render the whole vehicle list on every pointer move.
  const applyDragTransforms = useCallback((session, deltaY) => {
    const dropIndex = resolveDropIndex({
      centers: session.centers,
      fromIndex: session.fromIndex,
      deltaY,
    });
    session.dropIndex = dropIndex;

    session.order.forEach((key, index) => {
      const node = nodesRef.current.get(key);
      if (!node) return;

      if (index === session.fromIndex) {
        node.style.transform = `translate3d(0, ${deltaY}px, 0) scale(${LIFT_SCALE})`;
        return;
      }

      let shift = 0;
      if (dropIndex > session.fromIndex && index > session.fromIndex && index <= dropIndex) {
        shift = -session.height;
      } else if (dropIndex < session.fromIndex && index >= dropIndex && index < session.fromIndex) {
        shift = session.height;
      }
      node.style.transform = shift ? `translate3d(0, ${shift}px, 0)` : "";
    });
  }, []);

  const stopAutoScroll = useCallback((session) => {
    if (!session) return;
    cancelRaf(session.rafId);
    session.rafId = null;
  }, []);

  // Keeps the list scrolling while the pointer rests near the top or bottom of
  // the scroll container, so a card can be dragged past the visible window.
  const updateAutoScroll = useCallback((session) => {
    const scrollEl = session.scrollEl;
    if (!scrollEl || typeof scrollEl.getBoundingClientRect !== "function") return;

    const rect = scrollEl.getBoundingClientRect();
    let direction = 0;
    if (session.clientY < rect.top + AUTO_SCROLL_EDGE) direction = -1;
    else if (session.clientY > rect.bottom - AUTO_SCROLL_EDGE) direction = 1;

    session.scrollDirection = direction;
    if (!direction) {
      stopAutoScroll(session);
      return;
    }
    if (session.rafId) return;

    const step = () => {
      const live = sessionRef.current;
      if (!live || !live.dragging || !live.scrollDirection) {
        stopAutoScroll(live || session);
        return;
      }
      scrollEl.scrollTop += live.scrollDirection * AUTO_SCROLL_STEP;
      applyDragTransforms(live, deltaFor(live));
      live.rafId = raf(step);
    };
    session.rafId = raf(step);
  }, [applyDragTransforms, deltaFor, stopAutoScroll]);

  const beginDrag = useCallback((session) => {
    const order = [...orderKeysRef.current];
    const fromIndex = order.indexOf(session.key);
    const draggedNode = nodesRef.current.get(session.key);
    if (fromIndex === -1 || !draggedNode) return false;

    const rectOf = (node) =>
      node && typeof node.getBoundingClientRect === "function" ? node.getBoundingClientRect() : null;

    const draggedRect = rectOf(draggedNode);
    session.order = order;
    session.fromIndex = fromIndex;
    session.dropIndex = fromIndex;
    session.centers = order.map((key) => {
      const rect = rectOf(nodesRef.current.get(key));
      return rect ? rect.top + rect.height / 2 : 0;
    });
    session.height = (draggedRect ? draggedRect.height : 0) + CARD_GAP;
    session.scrollEl =
      typeof draggedNode.closest === "function"
        ? draggedNode.closest(SCROLL_CONTAINER_SELECTOR)
        : null;
    session.startScrollTop = session.scrollEl ? session.scrollEl.scrollTop : 0;
    session.dragging = true;

    order.forEach((key) => {
      const node = nodesRef.current.get(key);
      if (!node) return;
      node.style.willChange = "transform";
      // The dragged card must track the pointer exactly; only its neighbours slide.
      node.style.transition = key === session.key ? "none" : SLIDE_TRANSITION;
    });

    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    setDragKey(session.key);
    return true;
  }, []);

  const clearCardStyles = useCallback(() => {
    nodesRef.current.forEach((node) => {
      if (!node) return;
      node.style.transition = "none";
      node.style.transform = "";
    });
    // Drop the "none" once the committed order has painted, so the next drag
    // animates again.
    raf(() => {
      nodesRef.current.forEach((node) => {
        if (!node) return;
        node.style.transition = "";
        node.style.willChange = "";
      });
    });
  }, []);

  const detachWindowListeners = useCallback(() => {
    const listeners = sessionRef.current?.listeners;
    if (!listeners) return;
    window.removeEventListener("pointermove", listeners.move);
    window.removeEventListener("pointerup", listeners.up);
    window.removeEventListener("pointercancel", listeners.cancel);
    window.removeEventListener("keydown", listeners.key);
  }, []);

  const endSession = useCallback((commit) => {
    const session = sessionRef.current;
    if (!session) return;

    stopAutoScroll(session);
    detachWindowListeners();
    sessionRef.current = null;

    // A press that never passed the movement threshold was a plain click.
    if (!session.dragging) return;

    if (commit && session.dropIndex !== session.fromIndex) {
      moveCardToIndex(session.key, session.dropIndex);
    }
    // Styles are cleared in the same task as the reorder, so the browser paints
    // the committed order once instead of flashing an intermediate frame.
    clearCardStyles();
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    setDragKey(null);
  }, [clearCardStyles, detachWindowListeners, moveCardToIndex, stopAutoScroll]);

  const handlePointerMove = useCallback((event) => {
    const session = sessionRef.current;
    if (!session) return;
    if (session.pointerId != null && event.pointerId != null && event.pointerId !== session.pointerId) {
      return;
    }

    session.clientY = event.clientY;

    if (!session.dragging) {
      if (Math.abs(event.clientY - session.startY) < DRAG_START_THRESHOLD) return;
      if (!beginDrag(session)) {
        endSession(false);
        return;
      }
    }

    // Stops the browser from turning the drag into a text selection or a scroll.
    if (event.cancelable) event.preventDefault();

    applyDragTransforms(session, deltaFor(session));
    updateAutoScroll(session);
  }, [applyDragTransforms, beginDrag, deltaFor, endSession, updateAutoScroll]);

  const handlePointerDown = useCallback((event, key) => {
    if (sessionRef.current) endSession(false);
    // Left button / touch / pen only.
    if (typeof event.button === "number" && event.button !== 0) return;
    if (!canStartDragFrom(event.target)) return;
    if (!nodesRef.current.get(key)) return;

    const listeners = {
      move: handlePointerMove,
      up: () => endSession(true),
      cancel: () => endSession(false),
      key: (keyEvent) => {
        if (keyEvent.key === "Escape") endSession(false);
      },
    };

    sessionRef.current = {
      key,
      pointerId: event.pointerId,
      startY: event.clientY,
      clientY: event.clientY,
      dragging: false,
      listeners,
      rafId: null,
    };

    window.addEventListener("pointermove", listeners.move, { passive: false });
    window.addEventListener("pointerup", listeners.up);
    window.addEventListener("pointercancel", listeners.cancel);
    window.addEventListener("keydown", listeners.key);
  }, [endSession, handlePointerMove]);

  // Clean up if the component unmounts mid-drag.
  useEffect(() => () => {
    const session = sessionRef.current;
    if (!session) return;
    cancelRaf(session.rafId);
    const listeners = session.listeners;
    if (listeners) {
      window.removeEventListener("pointermove", listeners.move);
      window.removeEventListener("pointerup", listeners.up);
      window.removeEventListener("pointercancel", listeners.cancel);
      window.removeEventListener("keydown", listeners.key);
    }
    sessionRef.current = null;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  const registerCardNode = useCallback((key, node) => {
    if (node) nodesRef.current.set(key, node);
    else nodesRef.current.delete(key);
  }, []);

  const cardDragProps = useCallback((key) => ({
    ref: (node) => registerCardNode(key, node),
    onPointerDown: (event) => handlePointerDown(event, key),
  }), [handlePointerDown, registerCardNode]);

  // The handle needs no pointer handlers of its own: the card's `onPointerDown`
  // starts the drag and treats the handle as a draggable part of the card. What
  // is left here is the keyboard equivalent — and because the handle's DOM node
  // moves with the card, focus is restored by data attribute after the re-render.
  const handleProps = useCallback((key) => ({
    "data-vehicle-drag-handle": key,
    onKeyDown: (event) => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();
      if (!moveCardBy(key, event.key === "ArrowUp" ? -1 : 1)) return;
      raf(() => {
        document.querySelector(`[data-vehicle-drag-handle="${key}"]`)?.focus();
      });
    },
  }), [moveCardBy]);

  const cancelDrag = useCallback(() => endSession(false), [endSession]);

  const getOrderSnapshot = useCallback(() => [...orderKeysRef.current], []);

  const resetOrderChanged = useCallback(({ adoptServerOrder = false } = {}) => {
    adoptServerOrderRef.current = adoptServerOrder;
    setOrderChanged(false);
  }, []);

  return {
    cards,
    dragKey,
    orderChanged,
    cardDragProps,
    handleProps,
    cancelDrag,
    getOrderSnapshot,
    resetOrderChanged,
  };
}

// Builds the comma-separated `VehicleGuIds` value for EditProfile_api.
//
// `orderSnapshot` is the card order captured when Save was clicked, so an
// in-flight refresh (bulk-add refetches the profile) cannot clobber it.
// Saved keys resolve straight to their GuId. A draft key has no GuId yet, so it
// resolves to the vehicle that the bulk-add just created, matched on
// registration name and consumed once so duplicates cannot double-bind.
// Anything the snapshot does not account for is appended in server order rather
// than dropped.
export const buildOrderedVehicleGuIds = ({ orderSnapshot = [], drafts = [], serverVehicles = [] }) => {
  const serverById = new Map();
  const availableByRegistration = new Map();

  serverVehicles.forEach((vehicle) => {
    const guId = vehicle?.VehicleGuId;
    if (!guId) return;
    serverById.set(savedCardKey(vehicle), guId);
    const registration = normalizeRegistration(vehicle.RegistrationName);
    if (!availableByRegistration.has(registration)) {
      availableByRegistration.set(registration, []);
    }
    availableByRegistration.get(registration).push(guId);
  });

  const draftsByKey = new Map(drafts.map((draft) => [draftCardKey(draft), draft]));
  const placed = new Set();
  const orderedGuIds = [];

  const place = (guId) => {
    if (!guId || placed.has(guId)) return;
    placed.add(guId);
    orderedGuIds.push(guId);
  };

  orderSnapshot.forEach((key) => {
    const savedGuId = serverById.get(key);
    if (savedGuId) {
      place(savedGuId);
      return;
    }

    const draft = draftsByKey.get(key);
    if (!draft) return;

    const candidates = availableByRegistration.get(normalizeRegistration(draft.VehicleNumber)) || [];
    const match = candidates.find((guId) => !placed.has(guId));
    place(match);
  });

  // Newly created vehicles the snapshot could not match, plus anything added
  // outside this save, keep the order the server returned them in.
  serverVehicles.forEach((vehicle) => place(vehicle?.VehicleGuId));

  return orderedGuIds.join(",");
};
